import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API, useAuth } from "../context/AuthContext";

/**
 * Super Admin → AI Monitoring.
 *
 * Read-only surface for observing the customer-facing AI (ChatGPT).
 * Backed by:
 *   • GET /api/admin/ai/monitoring     — per-call log with filters + summary
 *   • GET /api/admin/ai/conversations  — per-session aggregate
 *
 * Safety invariants baked in:
 *   1. Only super_admin can reach this page (auth-gate below).
 *   2. Prompt / response text is NEVER stored → never displayed here.
 *   3. No API keys / secrets / card data / env vars are surfaced.
 */
export default function AIMonitoring() {
  const { user, authHeader, loading } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "super_admin") { nav("/"); return; }
  }, [loading, user?.id, nav]);

  if (loading || !user || user.role !== "super_admin") return null;

  return (
    <div className="App" data-testid="ai-monitoring-page">
      <Header hideSearch />
      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.25em]" style={{ color: "var(--gf-text-muted)" }}>Super Admin</div>
            <h1 className="font-display text-4xl mt-1" style={{ color: "var(--gf-text)" }}>AI monitoring</h1>
            <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--gf-text-muted)" }}>
              Read-only view of the customer AI. Prompt and response text are never stored, so nothing sensitive is shown here — only sizes, tokens, latency and error status per call and per conversation.
            </p>
          </div>
        </div>

        <div className="mt-8">
          <SummaryCards authHeader={authHeader} />
        </div>

        <div className="mt-10">
          <Conversations authHeader={authHeader} />
        </div>

        <div className="mt-10">
          <CallsTable authHeader={authHeader} />
        </div>
      </div>
      <Footer />
    </div>
  );
}

function SummaryCards({ authHeader }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ai/monitoring`, { params: { limit: 1 }, headers: authHeader() });
      setSummary(r.data.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AI summary");
    } finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { refresh(); }, [refresh]);

  const cfg = summary?.openai_configured;
  const fp = summary?.openai_key_fingerprint || {};
  const s24 = summary?.last_24h || {};
  const s1 = summary?.last_1h || {};

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ai-summary-cards">
      <Card label="OpenAI configured" testid="ai-summary-configured">
        <div className="flex items-center gap-2">
          <span style={{ color: cfg ? "#16a34a" : "#dc2626" }}>{cfg ? "Yes" : "No"}</span>
          {cfg && fp.prefix ? (
            <span className="text-xs font-mono" style={{ color: "var(--gf-text-muted)" }}>
              {fp.prefix}…{fp.last4}
            </span>
          ) : null}
        </div>
      </Card>
      <Card label="Calls (last 24h)" testid="ai-summary-24h-calls">
        <div className="font-display text-3xl" style={{ color: "var(--gf-text)" }}>{s24.calls ?? "—"}</div>
        <div className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
          {s24.errors ?? 0} errors · {(s24.tokens ?? 0).toLocaleString()} tokens
        </div>
      </Card>
      <Card label="Avg latency (24h)" testid="ai-summary-latency">
        <div className="font-display text-3xl" style={{ color: "var(--gf-text)" }}>{s24.avg_latency_ms ?? "—"} <span className="text-sm">ms</span></div>
      </Card>
      <Card label="Calls (last 1h)" testid="ai-summary-1h-calls">
        <div className="font-display text-3xl" style={{ color: "var(--gf-text)" }}>{s1.calls ?? "—"}</div>
        <div className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>
          {s1.errors ?? 0} errors
        </div>
      </Card>
      {loading ? (<div className="col-span-full text-xs" style={{ color: "var(--gf-text-muted)" }}>Loading…</div>) : null}
    </div>
  );
}

function Card({ label, children, testid }) {
  return (
    <div className="rounded-2xl border p-4" data-testid={testid} style={{ borderColor: "var(--gf-border)", background: "var(--gf-surface)" }}>
      <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--gf-text-muted)" }}>{label}</div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Conversations({ authHeader }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [sinceHours, setSinceHours] = useState(168);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ai/conversations`, {
        params: { q: q || undefined, errors_only: errorsOnly, since_hours: sinceHours, limit: 100 },
        headers: authHeader(),
      });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load conversations");
    } finally { setLoading(false); }
  }, [authHeader, q, errorsOnly, sinceHours]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <section data-testid="ai-conversations-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl" style={{ color: "var(--gf-text)" }}>Recent conversations</h2>
          <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>Aggregated by session. Prompt/response text is not stored — only counts, tokens, latency, and error status.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            data-testid="ai-conversations-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search session id or actor id…"
            className="text-sm rounded-md border bg-transparent px-3 py-1.5 outline-none focus:border-[var(--gf-accent)]"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          />
          <select
            data-testid="ai-conversations-window"
            value={sinceHours}
            onChange={(e) => setSinceHours(parseInt(e.target.value, 10))}
            className="text-sm rounded-md border bg-transparent px-3 py-1.5 outline-none"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >
            <option value={1}>Last 1 hour</option>
            <option value={24}>Last 24 hours</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
          <label className="text-xs flex items-center gap-2 rounded-md border px-3 py-1.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }}>
            <input data-testid="ai-conversations-errors-only" type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} />
            Errors only
          </label>
          <button data-testid="ai-conversations-refresh" onClick={refresh}
            className="text-xs rounded-md border px-3 py-1.5"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border overflow-hidden" style={{ borderColor: "var(--gf-border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.2em]" style={{ background: "var(--gf-surface-2)", color: "var(--gf-text-muted)" }}>
              <th className="text-left px-3 py-2">Session</th>
              <th className="text-left px-3 py-2">Actor</th>
              <th className="text-left px-3 py-2">Model</th>
              <th className="text-right px-3 py-2">Turns</th>
              <th className="text-right px-3 py-2">Tokens</th>
              <th className="text-right px-3 py-2">Avg latency</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Last activity</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-4 text-xs" style={{ color: "var(--gf-text-muted)" }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-4 text-xs" style={{ color: "var(--gf-text-muted)" }}>No conversations match.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.session_id} data-testid={`ai-conv-row-${r.session_id}`} style={{ borderTop: "1px solid var(--gf-border)" }}>
                <td className="px-3 py-2 font-mono text-xs" style={{ color: "var(--gf-text)" }} title={r.session_id}>{(r.session_id || "").slice(0, 24)}</td>
                <td className="px-3 py-2 text-xs" style={{ color: "var(--gf-text)" }}>
                  {r.actor ? (
                    <>
                      <div>{r.actor.business_name || r.actor.email}</div>
                      <div className="text-[10px]" style={{ color: "var(--gf-text-muted)" }}>{r.actor.role}</div>
                    </>
                  ) : (
                    <span style={{ color: "var(--gf-text-muted)" }}>anonymous</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs font-mono" style={{ color: "var(--gf-text-muted)" }}>{r.model || "—"}</td>
                <td className="px-3 py-2 text-xs text-right" style={{ color: "var(--gf-text)" }}>{r.call_count}</td>
                <td className="px-3 py-2 text-xs text-right" style={{ color: "var(--gf-text)" }}>{(r.tokens_total || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs text-right" style={{ color: "var(--gf-text)" }}>{r.avg_latency_ms} ms</td>
                <td className="px-3 py-2 text-xs">
                  {r.error_count > 0 ? (
                    <span data-testid="ai-conv-error-pill" style={{ color: "#dc2626" }}>
                      {r.error_count} error{r.error_count === 1 ? "" : "s"}{r.last_error_status ? ` · HTTP ${r.last_error_status}` : ""}
                    </span>
                  ) : (
                    <span style={{ color: "#16a34a" }}>OK</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: "var(--gf-text-muted)" }}>{formatTs(r.last_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CallsTable({ authHeader }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [purpose, setPurpose] = useState("");
  const [model, setModel] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [sinceHours, setSinceHours] = useState(24);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 50;

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/ai/monitoring`, {
        params: {
          q: q || undefined,
          purpose: purpose || undefined,
          model: model || undefined,
          errors_only: errorsOnly,
          since_hours: sinceHours,
          limit,
          skip: page * limit,
        },
        headers: authHeader(),
      });
      setRows(r.data.rows || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AI call log");
    } finally { setLoading(false); }
  }, [authHeader, q, purpose, model, errorsOnly, sinceHours, page]);

  useEffect(() => { refresh(); }, [refresh]);

  const purposes = useMemo(() => Array.from(new Set(rows.map((r) => r.purpose).filter(Boolean))), [rows]);
  const models = useMemo(() => Array.from(new Set(rows.map((r) => r.model).filter(Boolean))), [rows]);

  return (
    <section data-testid="ai-calls-section">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl" style={{ color: "var(--gf-text)" }}>AI call log</h2>
          <p className="text-xs mt-1" style={{ color: "var(--gf-text-muted)" }}>One row per OpenAI API call. Prompts and completions are never stored.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            data-testid="ai-calls-search"
            value={q}
            onChange={(e) => { setPage(0); setQ(e.target.value); }}
            placeholder="Search actor / session / model / purpose…"
            className="text-sm rounded-md border bg-transparent px-3 py-1.5 outline-none focus:border-[var(--gf-accent)]"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          />
          <select data-testid="ai-calls-purpose" value={purpose} onChange={(e) => { setPage(0); setPurpose(e.target.value); }}
            className="text-sm rounded-md border bg-transparent px-3 py-1.5 outline-none"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            <option value="">All purposes</option>
            <option value="customer_chat">customer_chat</option>
            {purposes.filter((p) => p && p !== "customer_chat").map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <select data-testid="ai-calls-model" value={model} onChange={(e) => { setPage(0); setModel(e.target.value); }}
            className="text-sm rounded-md border bg-transparent px-3 py-1.5 outline-none"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            <option value="">All models</option>
            {models.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select data-testid="ai-calls-window" value={sinceHours} onChange={(e) => { setPage(0); setSinceHours(parseInt(e.target.value, 10)); }}
            className="text-sm rounded-md border bg-transparent px-3 py-1.5 outline-none"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>
            <option value={1}>Last 1 hour</option>
            <option value={24}>Last 24 hours</option>
            <option value={168}>Last 7 days</option>
            <option value={720}>Last 30 days</option>
          </select>
          <label className="text-xs flex items-center gap-2 rounded-md border px-3 py-1.5" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text-muted)" }}>
            <input data-testid="ai-calls-errors-only" type="checkbox" checked={errorsOnly} onChange={(e) => { setPage(0); setErrorsOnly(e.target.checked); }} />
            Errors only
          </label>
        </div>
      </div>

      <div className="mt-4 rounded-2xl border overflow-hidden" style={{ borderColor: "var(--gf-border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.2em]" style={{ background: "var(--gf-surface-2)", color: "var(--gf-text-muted)" }}>
              <th className="text-left px-3 py-2">Time</th>
              <th className="text-left px-3 py-2">Session</th>
              <th className="text-left px-3 py-2">Actor</th>
              <th className="text-left px-3 py-2">Purpose</th>
              <th className="text-left px-3 py-2">Model</th>
              <th className="text-right px-3 py-2">Tokens</th>
              <th className="text-right px-3 py-2">Latency</th>
              <th className="text-left px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-4 text-xs" style={{ color: "var(--gf-text-muted)" }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-4 text-xs" style={{ color: "var(--gf-text-muted)" }}>No AI calls match your filters yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`ai-call-row-${r.id}`} style={{ borderTop: "1px solid var(--gf-border)" }}>
                <td className="px-3 py-2 text-xs" style={{ color: "var(--gf-text-muted)" }}>{formatTs(r.started_at)}</td>
                <td className="px-3 py-2 text-xs font-mono" style={{ color: "var(--gf-text)" }} title={r.session_id || ""}>{(r.session_id || "—").slice(0, 18)}</td>
                <td className="px-3 py-2 text-xs" style={{ color: "var(--gf-text)" }}>
                  {r.actor ? (
                    <>
                      <div>{r.actor.business_name || r.actor.email}</div>
                      <div className="text-[10px]" style={{ color: "var(--gf-text-muted)" }}>{r.actor.role}</div>
                    </>
                  ) : (
                    <span style={{ color: "var(--gf-text-muted)" }}>anonymous</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs" style={{ color: "var(--gf-text)" }}>{r.purpose || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono" style={{ color: "var(--gf-text-muted)" }}>{r.model || "—"}</td>
                <td className="px-3 py-2 text-xs text-right" style={{ color: "var(--gf-text)" }}>{(r.total_tokens || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs text-right" style={{ color: "var(--gf-text)" }}>{r.latency_ms || 0} ms</td>
                <td className="px-3 py-2 text-xs">
                  {r.success === false ? (
                    <span style={{ color: "#dc2626" }}>
                      {r.error_type || "error"}{r.error_status ? ` · HTTP ${r.error_status}` : ""}
                    </span>
                  ) : (
                    <span style={{ color: "#16a34a" }}>OK</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs" style={{ color: "var(--gf-text-muted)" }}>
        <div>{total.toLocaleString()} total · showing {page * limit + 1}–{Math.min((page + 1) * limit, total)}</div>
        <div className="flex gap-2">
          <button
            data-testid="ai-calls-prev"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-md border px-3 py-1.5 disabled:opacity-40"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >Prev</button>
          <button
            data-testid="ai-calls-next"
            disabled={(page + 1) * limit >= total}
            onClick={() => setPage((p) => p + 1)}
            className="rounded-md border px-3 py-1.5 disabled:opacity-40"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >Next</button>
        </div>
      </div>
    </section>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
